import React from 'react';
import { Minus, Plus, Users } from 'lucide-react';
import { WaitlistEntry } from '../types';

interface WaitlistQuantityControlProps {
  quantity: number;
  onQuantityChange: (quantity: number) => void;
  onToggle: () => void;
  waitlistEntry?: WaitlistEntry;
  waitingQuantity?: number;
  disabled?: boolean;
  variant?: 'compact' | 'full';
}

const MAX_WAITLIST_QUANTITY = 99;

const WaitlistQuantityControl: React.FC<WaitlistQuantityControlProps> = ({
  quantity,
  onQuantityChange,
  onToggle,
  waitlistEntry,
  waitingQuantity = 0,
  disabled = false,
  variant = 'compact',
}) => {
  const isFull = variant === 'full';
  const remainingQuantity = waitlistEntry?.remainingQuantity
    ?? waitlistEntry?.quantity
    ?? 0;
  const fulfilledQuantity = waitlistEntry?.fulfilledQuantity ?? 0;
  const unitsAhead = Math.max(0, (waitlistEntry?.position ?? 1) - 1);

  const stopPropagation = (event: React.MouseEvent) => {
    event.stopPropagation();
  };

  if (waitlistEntry) {
    return (
      <button
        type="button"
        onClick={(event) => {
          stopPropagation(event);
          onToggle();
        }}
        disabled={disabled}
        className={`${isFull ? 'h-14 w-full rounded-2xl px-4 text-sm' : 'min-h-9 rounded-xl px-3 text-[11px]'} flex items-center justify-center gap-1.5 border border-amber-200 bg-amber-50 font-black text-amber-700 transition-all active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50`}
      >
        <Users size={isFull ? 17 : 13} />
        {disabled
          ? '처리 중'
          : fulfilledQuantity > 0
            ? `${fulfilledQuantity}개 반영 · ${remainingQuantity}개 대기 · 취소`
            : unitsAhead > 0
              ? `내 앞 ${unitsAhead}개 · ${remainingQuantity}개 대기 · 취소`
              : `${remainingQuantity}개 대기 중 · 취소`}
      </button>
    );
  }

  return (
    <div
      className={`${isFull ? 'flex-col gap-2' : 'flex-wrap justify-end gap-1.5'} flex items-center`}
      onClick={stopPropagation}
    >
      {waitingQuantity > 0 && (
        <span className={`${isFull ? 'text-xs' : 'text-[10px]'} font-black text-violet-600`}>
          현재 {waitingQuantity}개 대기
        </span>
      )}
      <div className={`${isFull ? 'w-full gap-2' : 'gap-1'} flex items-center`}>
        <div className={`${isFull ? 'h-12 flex-1 rounded-2xl' : 'h-9 rounded-xl'} flex items-center border border-gray-200 bg-white px-1 shadow-sm`}>
          <button
            type="button"
            aria-label="줄서기 수량 줄이기"
            onClick={() => onQuantityChange(Math.max(1, quantity - 1))}
            disabled={disabled || quantity <= 1}
            className={`${isFull ? 'h-10 w-10' : 'h-7 w-7'} flex items-center justify-center rounded-lg text-gray-700 transition-colors hover:bg-gray-100 disabled:text-gray-200`}
          >
            <Minus size={isFull ? 17 : 14} strokeWidth={3} />
          </button>
          <span className={`${isFull ? 'min-w-12 text-base' : 'min-w-7 text-sm'} text-center font-black text-gray-900`}>
            {quantity}개
          </span>
          <button
            type="button"
            aria-label="줄서기 수량 늘리기"
            onClick={() =>
              onQuantityChange(Math.min(MAX_WAITLIST_QUANTITY, quantity + 1))
            }
            disabled={disabled || quantity >= MAX_WAITLIST_QUANTITY}
            className={`${isFull ? 'h-10 w-10' : 'h-7 w-7'} flex items-center justify-center rounded-lg text-gray-700 transition-colors hover:bg-gray-100 disabled:text-gray-200`}
          >
            <Plus size={isFull ? 17 : 14} strokeWidth={3} />
          </button>
        </div>
        <button
          type="button"
          onClick={onToggle}
          disabled={disabled}
          className={`${isFull ? 'h-12 flex-1 rounded-2xl text-sm' : 'h-9 rounded-xl px-3 text-[11px]'} flex items-center justify-center gap-1.5 bg-gray-900 font-black text-white transition-all active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50`}
        >
          <Users size={isFull ? 17 : 13} />
          {disabled ? '처리 중' : `${quantity}개 줄서기`}
        </button>
      </div>
      {isFull && (
        <p className="text-center text-[11px] font-bold text-gray-400">
          재고가 생기는 수량부터 자동으로 주문에 반영됩니다.
        </p>
      )}
    </div>
  );
};

export default WaitlistQuantityControl;
